/**
 * The time and attendance tables (piece 15; decisions 0115, 0124, 0127): a
 * registered clock terminal, a person's own credential to it, the fixed
 * 168-hour workweek, the holiday rows the billing engine reads by date, and
 * business hours. The behaviour over them lands with HR; the rules that are
 * arithmetic live here.
 */
import { t } from '../strings';

export const CREDENTIAL_KINDS = ['PIN', 'Badge', 'Biometric'] as const;
export type CredentialKind = (typeof CREDENTIAL_KINDS)[number];

/**
 * 🔒 The clock's own lockout (piece 15; decision 1250, on the owner's order of
 * 2026-09-28): this many refused punches against one employment inside the
 * window refuse every further punch for it until the oldest leaves the
 * window. Edited on Settings › People › Logins beside the login lockout pair.
 */
export const CLOCK_LOCKOUT_THRESHOLD_KEY = 'attendance.clockLockoutThreshold';
export const CLOCK_LOCKOUT_WINDOW_KEY = 'attendance.clockLockoutWindowMinutes';
export const DEFAULT_CLOCK_LOCKOUT_THRESHOLD = 5;
export const DEFAULT_CLOCK_LOCKOUT_WINDOW_MINUTES = 15;

/** 🔨 An unregistered device takes no punches: a punch is offered only against a row that exists and is active. */
export function deviceMayPunch(device: { isActive: boolean } | null | undefined): boolean {
  return Boolean(device && device.isActive);
}

/** One registered clock device as Settings › HR › Work Calendar lists it (decision 1607). */
export interface ClockDeviceRow {
  id: string;
  siteId: string;
  siteName: string;
  label: string;
  deviceKind: string;
  isActive: boolean;
  /** The registration day, `YYYY-MM-DD`. */
  registeredOn: string;
  registeredByName: string;
}

/** The Clock devices section's answer: every device, and the gated sites one may be registered at. */
export interface ClockDevicesAnswer {
  rows: ClockDeviceRow[];
  sites: { id: string; name: string }[];
}

/** One holiday as Settings › HR › Work Calendar lists it (decision 1390): organisation-wide, or one state's. */
export interface HolidayRow {
  id: string;
  name: string;
  /** The day, `YYYY-MM-DD`. */
  date: string;
  isObserved: boolean;
  /** Null for an organisation-wide holiday; otherwise the two-letter state the row is for. */
  stateCode: string | null;
}

/** The Holidays section's answer for one year, and the states a holiday may be kept for. */
export interface HolidaysAnswer {
  year: number;
  rows: HolidayRow[];
  states: { jurisdictionId: string; stateCode: string }[];
}

/** What a terminal is called when the form names no kind. */
export const DEFAULT_CLOCK_DEVICE_KIND = 'Tablet';

/** 🔨 A workweek change takes effect forward only, needs a reason, and is an AuditEvent (decision 0124). */
export function workweekChangeProblems(input: { effectiveFrom: Date; reason: string | null | undefined }, now: Date): string[] {
  const problems: string[] = [];
  if (input.effectiveFrom.getTime() < startOfDay(now).getTime()) problems.push('a workweek change takes effect today or later, never backwards');
  if (!input.reason?.trim()) problems.push('a workweek change names its reason');
  return problems;
}

function startOfDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

/**
 * 🔨 `isObserved` lets the premium follow the observed day, and a computed
 * rule is not used: the generator proposes the observed day, and the row is
 * editable after. A Saturday holiday is observed on the Friday, a Sunday one
 * on the Monday.
 */
export function observedDate(date: Date): { date: Date; isObserved: boolean } {
  const day = date.getUTCDay();
  if (day === 6) return { date: new Date(date.getTime() - 86_400_000), isObserved: true };
  if (day === 0) return { date: new Date(date.getTime() + 86_400_000), isObserved: true };
  return { date, isObserved: false };
}

/** The nth weekday of a month, for the holidays that float: the fourth Thursday of November. */
export function nthWeekday(year: number, month: number, weekday: number, n: number): Date {
  const first = new Date(Date.UTC(year, month, 1));
  const offset = (weekday - first.getUTCDay() + 7) % 7;
  return new Date(Date.UTC(year, month, 1 + offset + (n - 1) * 7));
}

export function lastWeekday(year: number, month: number, weekday: number): Date {
  const last = new Date(Date.UTC(year, month + 1, 0));
  const offset = (last.getUTCDay() - weekday + 7) % 7;
  return new Date(Date.UTC(year, month, last.getUTCDate() - offset));
}

export interface ProposedHoliday {
  name: string;
  date: Date;
  isObserved: boolean;
}

/** The United States federal holidays of one year, each on its observed day where the calendar moves it, generated a year ahead and then editable. */
export function federalHolidays(year: number): ProposedHoliday[] {
  const fixed = [
    { name: "New Year's Day", date: new Date(Date.UTC(year, 0, 1)) },
    { name: 'Juneteenth', date: new Date(Date.UTC(year, 5, 19)) },
    { name: 'Independence Day', date: new Date(Date.UTC(year, 6, 4)) },
    { name: 'Veterans Day', date: new Date(Date.UTC(year, 10, 11)) },
    { name: 'Christmas Day', date: new Date(Date.UTC(year, 11, 25)) },
  ].map((h) => ({ name: h.name, ...observedDate(h.date) }));
  const floating: ProposedHoliday[] = [
    { name: 'Martin Luther King Jr. Day', date: nthWeekday(year, 0, 1, 3), isObserved: false },
    { name: "Presidents' Day", date: nthWeekday(year, 1, 1, 3), isObserved: false },
    { name: 'Memorial Day', date: lastWeekday(year, 4, 1), isObserved: false },
    { name: 'Labor Day', date: nthWeekday(year, 8, 1, 1), isObserved: false },
    { name: 'Columbus Day', date: nthWeekday(year, 9, 1, 2), isObserved: false },
    { name: 'Thanksgiving Day', date: nthWeekday(year, 10, 4, 4), isObserved: false },
  ];
  return [...fixed, ...floating].sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * 🔒 One business-hours period as Settings › HR › Work Calendar lists it
 * (decision 3860): a day of the week, 0 Sunday to 6 Saturday, its opening and
 * closing wall clock in the organization's zone, and the days it is in force,
 * both ends inclusive, `YYYY-MM-DD`.
 */
export interface BusinessHoursRow {
  id: string;
  dayOfWeek: number;
  opensAt: string;
  closesAt: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

/** The Business hours section's answer: every period, newest first within each day. */
export interface BusinessHoursAnswer {
  rows: BusinessHoursRow[];
  /** The Organization's own calendar day (T2361; absent from an older answer): the day a change takes effect opens on, since the server refuses a change against it. */
  today?: string;
}

/** One workweek definition as the Workweek section lists it (decisions 0124, 3860): organization-wide, or one state's. */
export interface WorkweekRow {
  id: string;
  startsOnDayOfWeek: number;
  startsAtTime: string;
  /** Null for the organization's own week; otherwise the two-letter state it is for. */
  stateCode: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
}

/** The Workweek section's answer, and the states a week may be defined for. */
export interface WorkweeksAnswer {
  rows: WorkweekRow[];
  states: { jurisdictionId: string; stateCode: string }[];
  /** The Organization's own calendar day (T2361), as the Business hours answer carries it. */
  today?: string;
}

/** The days of the week by the JavaScript number, Sunday first, as the Work Calendar names them. */
export const WEEKDAY_LABEL_KEYS = ['settings.hr.day.sunday', 'settings.hr.day.monday', 'settings.hr.day.tuesday', 'settings.hr.day.wednesday', 'settings.hr.day.thursday', 'settings.hr.day.friday', 'settings.hr.day.saturday'] as const;

/** A day of the week in words; a number outside 0 to 6 reads as it came. */
export function weekdayLabel(dayOfWeek: number): string {
  const key = WEEKDAY_LABEL_KEYS[dayOfWeek];
  return key ? t(key) : String(dayOfWeek);
}

/** The close that ends a day at midnight, which sorts after every other wall clock. */
export const END_OF_DAY = '24:00';

/** The wall clocks the Work Calendar offers, every half hour from midnight; a closing time may also be the day's end. */
export function timeOfDayChoices(closing = false): string[] {
  const out: string[] = [];
  for (let minutes = closing ? 30 : 0; minutes < 24 * 60; minutes += 30) out.push(`${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`);
  return closing ? [...out, END_OF_DAY] : out;
}

/** A stored `HH:MM` as an American reader reads it: 8:00 AM, 5:30 PM, and the day's end as Midnight. */
export function clockLabel(hhmm: string): string {
  if (hhmm === END_OF_DAY) return t('settings.hr.businessHours.midnight');
  const match = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!match) return hhmm;
  const hour = Number(match[1]);
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${match[2]} ${hour < 12 ? 'AM' : 'PM'}`;
}

/**
 * 🔨 Why a business-hours change is refused, or an empty list (decision 3860):
 * at least one day, wall clocks written `HH:MM`, a day that closes after it
 * opens unless the change closes the days, and a start of today or later, the
 * organization's today, so a past invoice keeps the hours it was priced on.
 */
export function businessHoursChangeProblems(input: { daysOfWeek: readonly number[]; opensAt: string; closesAt: string; closed: boolean; effectiveFrom: Date }, today: Date): string[] {
  const problems: string[] = [];
  if (input.daysOfWeek.length === 0) problems.push('a business-hours change names at least one day');
  if (input.daysOfWeek.some((day) => !Number.isInteger(day) || day < 0 || day > 6)) problems.push('a day is 0 to 6, Sunday to Saturday');
  if (!input.closed) {
    const clock = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (!clock.test(input.opensAt) || !(clock.test(input.closesAt) || input.closesAt === END_OF_DAY)) problems.push('the hours read HH:MM');
    else if (input.opensAt >= input.closesAt) problems.push('the day closes after it opens');
  }
  if (Number.isNaN(input.effectiveFrom.getTime())) problems.push('the hours take effect on a day written YYYY-MM-DD');
  else if (input.effectiveFrom.getTime() < startOfDay(today).getTime()) problems.push('a business-hours change takes effect today or later, never backwards');
  return problems;
}
